import { useEffect, useState } from "react";
import {
  AboutDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { Button, message, Modal, Upload } from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaEdit, FaRegWindowClose } from "react-icons/fa";
import { AboutService } from "../../services/about.service";
import { UploadOutlined } from "@ant-design/icons";
import RichTextEditor from "react-rte";

const About = () => {
  const [about, setAbout] = useState<AboutDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [imageList, setImageList] = useState<any[]>([]);
  const [editorState, setEditorState] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    setLoading(true);
    AboutService.getAbout()
      .then((response) => {
        setAbout(response?.data?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleEditorChange = (value: any) => {
    setEditorState((prev) => ({
      ...prev,
      [langCode]: value,
    }));

    const textValue = value.toString("html");

    handleTranslationChange(textValue, "content");
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        title: "",
        description: "",
        content: "",
        images: [] as string[],
        translations: [] as {
          lang_code: string;
          title: string;
          description: string;
          content: string;
        }[],
      },
      // validationSchema: Yup.object({
      //   title: Yup.string().required("Haqqımızda başlığı tələb olunur"),
      //   description: Yup.string().required("Haqqımızda təsviri tələb olunur"),
      //   content: Yup.string().required("Haqqımızda kontent tələb olunur"),
      // }),
      onSubmit: async (values) => {
        try {
          const azTranslation = {
            lang_code: "AZ",
            title: values.title,
            description: values.description,
            content: values.content,
          };
          const updatedTranslations = [
            azTranslation,
            ...(Array.isArray(values.translations)
              ? values.translations?.filter((t) => t.lang_code !== "AZ")
              : []),
          ];
          const finalValues: any = {
            ...values,
            translations: updatedTranslations,
          };
          if (about) {
            const isEqual =
              about?.title === values.title &&
              about?.description === values.description &&
              about?.content === values.content &&
              about?.images?.toString() === values.images?.toString() &&
              about?.translations === values.translations;
            if (isEqual) {
              message.info("Dəyişiklik edilmədi.");
            } else {
              const response = await AboutService.updateAbout(finalValues);

              if (response.status === 200) {
                setIsModalVisible(false);
                resetForm();
                message.success("Haqqımızda uğurla yeniləndi!");
                AboutService.getAbout().then((response) => {
                  setAbout(response?.data?.data);
                });
              } else {
                message.error(
                  response.errorMessage || "Haqqımızda yeniləmək alınmadı."
                );
              }
            }
          }
        } catch (error) {
          message.error("Nələrsə səhv getdi.");
        } finally {
          setLoading(false);
        }
      },
    });

  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "title") {
        setFieldValue("title", value);
      } else if (field === "description") {
        setFieldValue("description", value);
      } else if (field === "content") {
        setFieldValue("content", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );
      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          title: field === "title" ? value : "",
          description: field === "description" ? value : "",
          content: field === "content" ? value : "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleLanguageButtonClick = (lang: string) => {
    setLangCode(lang);
    const existingTranslation = values.translations.find(
      (t) => t.lang_code === lang
    );

    if (!existingTranslation && lang !== "AZ") {
      setFieldValue("translations", [...values.translations]);
    }

    const currentContent = editorState[langCode]?.toString("html");

    if (langCode === "AZ") {
      handleTranslationChange(currentContent, "content");
    } else {
      const updatedTranslations = values.translations.map((t) =>
        t.lang_code === langCode ? { ...t, content: currentContent } : t
      );
      setFieldValue("translations", updatedTranslations);
    }
  };

  const getBase64 = (file: File, callback: (base64: string) => void) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result as string);
    reader.onerror = () => message.error("Faylı yükləmək alınmadı.");
  };

  const handleFileUpload = (file: File) => {
    getBase64(file, (base64: string) => {
      setImageList((prevList) => {
        const newList = [
          ...prevList,
          {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            url: base64,
          },
        ];

        setFieldValue(
          "images",
          newList.map((img) => img.url)
        );

        return newList;
      });
    });
    return false;
  };

  const handleImageDelete = (uid: string) => {
    const updatedImageList = imageList.filter((img) => img.uid !== uid);
    setImageList(updatedImageList);
    const updatedImages = updatedImageList.map((img) => img.url);
    setFieldValue("images", updatedImages);
  };

  useEffect(() => {
    if (about?.images?.length) {
      setImageList(
        about.images.map((img, index) => ({
          uid: `${index}`,
          name: `image-${index}`,
          url: img,
        }))
      );
    }
  }, [about]);

  const handleEditClick = () => {
    setIsModalVisible(true);
    setFieldValue("translations", about?.translations || []);
    setFieldValue("title", about?.title || "");
    setFieldValue("description", about?.description || "");
    setFieldValue("content", about?.content || "");
    setFieldValue("images", about?.images || []);
    setLangCode("AZ");
  };

  useEffect(() => {
    if (about) {
      const initialEditorState: { [key: string]: any } = {
        AZ: about.content
          ? RichTextEditor.createValueFromString(about.content, "html")
          : RichTextEditor.createEmptyValue(),
      };
      about.translations?.forEach((translation) => {
        initialEditorState[translation.lang_code] = translation.content
          ? RichTextEditor.createValueFromString(translation.content, "html")
          : RichTextEditor.createEmptyValue();
      });

      setEditorState(initialEditorState);
    }
  }, [about]);

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Haqqımızda</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Haqqımızda</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleEditClick}>
              <FaEdit size={20} />
            </MainButton>
            <div className="pages_table">
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                  gap: "24px",
                  marginTop: "30px",
                  marginBottom: "20px",
                }}
                className="images"
              >
                {about?.images.map((image, index) => {
                  return (
                    <div key={index}>
                      <img
                        width={"500px"}
                        height={"400px"}
                        style={{
                          objectFit: "cover",
                          borderRadius: "12px",
                        }}
                        src={image}
                        alt=""
                      />
                    </div>
                  );
                })}
              </div>

              <>
                <div className="title">{about?.title}</div>
                <div style={{ margin: "16px 0" }} className="description">
                  {about?.description}
                </div>
                <div
                  className="content"
                  dangerouslySetInnerHTML={{
                    __html: about?.content || "",
                  }}
                />
              </>
            </div>
          </div>
        </div>
      )}

      <Modal
        title={"Haqqımızda səhifəsini yeniləyin"}
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="langs">
            {langs.map((lang, index) => {
              return (
                <div key={index}>
                  <button
                    className={lang.code === langCode ? "active_lang_btn" : ""}
                    type="button"
                    onClick={() => handleLanguageButtonClick(lang.code)}
                  >
                    {lang.name}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="form_input">
            <label className="label" htmlFor="title">
              Haqqımızda başlıq {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="title"
                id="title"
                placeholder="Haqqımızda başlıq"
                value={
                  langCode === "AZ"
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                      ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="Description">
              Haqqımızda təsvir {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="description"
                id="description"
                placeholder="Haqqımızda təsvir"
                value={
                  langCode === "AZ"
                    ? values.description
                    : values.translations?.find((t) => t.lang_code === langCode)
                      ?.description || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>

          <div className="form_input">
            <label className="ck_label" htmlFor="Description">
              Haqqımızda kontenti {langCode}
            </label>
            <RichTextEditor
              value={editorState[langCode] || RichTextEditor.createEmptyValue()}
              onChange={handleEditorChange}
            />
            <ErrorMessage error={errors.content} touched={touched.content} />
          </div>

          <div className="form_input">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: "24px",
                marginTop: "30px",
                marginBottom: "20px",
              }}
              className="image-preview"
            >
              {imageList.map((img) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "2px",
                  }}
                  key={img.uid}
                  className="image-preview-item"
                >
                  <img
                    height={"400px"}
                    src={img.url}
                    style={{ objectFit: "cover", borderRadius: "12px",maxWidth:"100%" }}
                    alt={img.name}
                  />
                  <div
                    onClick={() => handleImageDelete(img.uid)}
                    style={{
                      fontSize: "25px",
                      color: "red",
                      cursor: "pointer",
                      marginLeft: "8px",
                      marginBottom: "-10px",
                    }}
                  >
                    <FaRegWindowClose />
                  </div>
                </div>
              ))}
            </div>
            <Upload
              name="images"
              multiple
              fileList={imageList.map((img) => ({
                uid: img.uid,
                name: img.name,
                url: img.url,
              }))}
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Şəkilləri seçin</Button>
            </Upload>
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {"Redaktə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default About;
